import { readPersistedAirlineRouterState } from "../transport/airlineTransportContext";
import { GUEST_PLATFORM_OFFER_TYPE } from "./toMarketplaceOfferType";
import { MarketplaceCurrentOffer } from "./types";

const normalize = (value?: string | null): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = String(value).trim();
  return trimmed || null;
};

const isExcludedOfferPath = (pathname: string): boolean => {
  return /\/confirmation\/?$/i.test(pathname) || /\/checkout(\/|$)/i.test(pathname);
};

const readOfferCodeFromNavState = (locationState: unknown): string | null => {
  const fromLocation = locationState as { shared?: { offer_code?: string | null } } | null;
  const fromLocationCode = normalize(fromLocation?.shared?.offer_code);
  if (fromLocationCode) {
    return fromLocationCode;
  }

  const persisted = readPersistedAirlineRouterState() as { shared?: { offer_code?: string | null } } | null;
  return normalize(persisted?.shared?.offer_code);
};

/** Prefer API `type` stored on nav state; fall back to path segment only if missing. */
const readOfferTypeFromNavState = (locationState: unknown): string | null => {
  const state = locationState as { meta?: { offerType?: string | null; transportType?: string | null } } | null;
  const fromLocation = normalize(state?.meta?.offerType) ?? normalize(state?.meta?.transportType);
  if (fromLocation) {
    return fromLocation;
  }

  const persisted = readPersistedAirlineRouterState() as {
    meta?: { offerType?: string | null; transportType?: string | null };
  } | null;
  return normalize(persisted?.meta?.offerType) ?? normalize(persisted?.meta?.transportType);
};

/**
 * Resolves the current marketplace offer identity from the URL (and airline nav state).
 * Transport `offer_type` comes from API `type` on navigation state when available.
 * Guest Platform list offers use `guest_platform_offer`.
 */
export const resolveMarketplaceOfferFromLocation = (
  pathname: string,
  locationState?: unknown
): MarketplaceCurrentOffer | null => {
  if (!pathname || isExcludedOfferPath(pathname)) {
    return null;
  }

  const transportMatch = pathname.match(/\/transport\/([^/]+)/i);
  if (transportMatch) {
    return {
      offer_type: readOfferTypeFromNavState(locationState) ?? normalize(transportMatch[1]),
      offer_code: readOfferCodeFromNavState(locationState)
    };
  }

  const offerMatch = pathname.match(/\/offers\/([^/]+)/i);
  if (offerMatch) {
    return {
      offer_type: GUEST_PLATFORM_OFFER_TYPE,
      offer_code: normalize(decodeURIComponent(offerMatch[1]))
    };
  }

  return null;
};
